import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import CADViewer from 'components/CADPreview/viewer';
import ProductFormModal from 'features/products/components/ProductFormModal';
import ProductStatusBadge from 'features/products/components/ProductStatusBadge';
import useGetProductById from 'features/products/hooks/useGetProductById';
import useGetProductVersion from 'features/products/hooks/useGetProductVersion';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import ProductVersionsSection from './components/ProductVersionsSection';

export default function ProductDetailPage() {
  const { productDocId } = useParams();
  const [openEditModal, setOpenEditModal] = useState(false);
  const {
    data: product,
    error,
    isLoading,
    refetch,
  } = useGetProductById(productDocId);
  const {
    data: activeVersion,
    isLoading: isActiveVersionLoading,
    error: activeVersionError,
    refetch: refetchActiveVersion,
  } = useGetProductVersion(productDocId, product?.activeVersionId);

  if (isLoading || isActiveVersionLoading) {
    return <StatusIndicator type="loading">Loading product...</StatusIndicator>;
  }

  if (error || activeVersionError) {
    return (
      <Alert type="error">
        {error?.message || activeVersionError?.message}
      </Alert>
    );
  }

  return (
    <>
      <Container
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <Button variant="primary" onClick={() => setOpenEditModal(true)}>
                Edit product
              </Button>
            )}
          >
            {product?.sku || 'Product'}
          </Header>
        )}
      >
        <SpaceBetween size="l">
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Name</Box>
              <div>{product?.name || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <ProductStatusBadge status={product?.status} />
            </div>
            <div>
              <Box variant="awsui-key-label">Description</Box>
              <div>{product?.description || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Label QR header</Box>
              <div>{product?.labelQrHeader || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Label QR link</Box>
              <div>
                {product?.labelQrUrl
                  ? <Link href={product.labelQrUrl} external>{product.labelQrUrl}</Link>
                  : '-'}
              </div>
            </div>
            <div>
              <Box variant="awsui-key-label">Created</Box>
              <div>{product?.createdAt?.toDate?.().toLocaleString() || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Updated</Box>
              <div>{product?.updatedAt?.toDate?.().toLocaleString() || '-'}</div>
            </div>
          </ColumnLayout>

          <Container header={<Header variant="h2">Active Manufacturing Summary</Header>}>
            <SpaceBetween direction="vertical" size="m">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Version</Box>
                  <div>{activeVersion?.id || '-'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Unit Price</Box>
                  <div>{typeof activeVersion?.unitPrice === 'number' ? `$${activeVersion.unitPrice.toFixed(2)}` : '-'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Lead Time</Box>
                  <div>{activeVersion?.leadtimeDays ? `${activeVersion.leadtimeDays} days` : '-'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Project Part</Box>
                  <div>{[activeVersion?.projectId, activeVersion?.pPartId].filter(Boolean).join('-') || '-'}</div>
                </div>
              </ColumnLayout>
              {activeVersion?.previewGlb?.[0]?.downloadURL && (
                <div>
                  <Box variant="awsui-key-label">3D Preview</Box>
                  <CADViewer
                    src={activeVersion.previewGlb[0].downloadURL}
                    style={{ width: '100%', height: 320, display: 'block' }}
                  />
                </div>
              )}
            </SpaceBetween>
          </Container>

          <ProductVersionsSection
            product={product}
            onRefreshProduct={refetch}
            onRefreshActiveVersion={refetchActiveVersion}
          />
        </SpaceBetween>
      </Container>

      {openEditModal && (
        <ProductFormModal
          open={openEditModal}
          isEditing
          initProduct={product}
          onClose={() => setOpenEditModal(false)}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
    </>
  );
}
